import { Canvas } from "@react-three/fiber";
import { PerspectiveCamera, Environment, MapControls } from "@react-three/drei";
import { useRef, useEffect, useMemo, memo } from "react";
import { useShallow } from "zustand/shallow";
import type { Box3 } from "three";
import Building from "./building/index";
import RoomLabels from "./roomLabels";
import useBuilding from "../../hooks/useBuilding";
import RouteLine from "../routeLine";
import FloorCameraController from "../floorCameraController";
import CameraBoundsController from "../cameraBoundsControl";
import RouteCameraController from "../routeCameraController";
import { useMapRouteStore } from "@/stores/mapRouteStore";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/apiClient";
import { directionSchema, type Direction } from "../../types/directionsTypes";
import { computeHighlightState } from "../../utils/highlightState";
import { computeFloorVisibility, type FloorVisibility } from "../../utils/floorVisibility";

/**
 * Props для SceneContent компонента
 */
interface SceneContentProps {
  controlsRef: React.MutableRefObject<any>;
  cameraPosition: [number, number, number] | undefined;
  cameraTarget: [number, number, number] | undefined;
  currentFloor: number;
  isCameraAnimating: boolean;
  bbox: Box3 | null;
  direction: Direction | null;
  selectedSegmentId: number | null;
  focusFullRoute: boolean;
  setFocusFullRoute: (v: boolean) => void;
  setCurrentFloor: (floor: number) => void;
  setIsCameraAnimating: (v: boolean) => void;
  buildingRef: React.RefObject<any>;
  isFloorManuallySelected: boolean;
  onBuildingLoad: (scene?: any) => void;
  routePoints: [number, number, number][] | null;
  floorVisibility: FloorVisibility;
}

/**
 * SceneContent - внутренний компонент Canvas с 3D содержимым сцены.
 */
const SceneContent = memo(function SceneContent({
  controlsRef,
  cameraPosition,
  cameraTarget,
  currentFloor,
  isCameraAnimating,
  bbox,
  direction,
  selectedSegmentId,
  focusFullRoute,
  setFocusFullRoute,
  setCurrentFloor,
  setIsCameraAnimating,
  buildingRef,
  isFloorManuallySelected,
  onBuildingLoad,
  routePoints,
  floorVisibility,
}: SceneContentProps) {
  return (
    <>
      <PerspectiveCamera makeDefault position={cameraPosition} />
      <MapControls
        ref={controlsRef}
        target={cameraTarget}
        enableZoom
        enablePan
        enableRotate
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 3}
        zoomSpeed={0.8}
        panSpeed={0.5}
        rotateSpeed={0.5}
      />
      <FloorCameraController
        controlsRef={controlsRef}
        floor={currentFloor}
        isCameraAnimating={isCameraAnimating}
      />
      <CameraBoundsController
        controlsRef={controlsRef}
        bbox={bbox}
        isCameraAnimating={isCameraAnimating}
      />
      <RouteCameraController
        controlsRef={controlsRef}
        direction={direction}
        selectedSegmentId={selectedSegmentId}
        focusFullRoute={focusFullRoute}
        setFocusFullRoute={setFocusFullRoute}
        setCurrentFloor={setCurrentFloor}
        setIsCameraAnimating={setIsCameraAnimating}
        bbox={bbox}
      />

      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 10, 5]} intensity={1.2} />

      <Building
        ref={buildingRef as any}
        direction={direction}
        selectedSegmentId={selectedSegmentId}
        currentFloor={currentFloor}
        isFloorManuallySelected={isFloorManuallySelected}
        onLoad={onBuildingLoad}
      />

      {/* Названия кабинетов */}
      <RoomLabels />

      {/* Окружение */}
      <Environment files="/hdri/potsdamer_platz_1k.hdr" />

      {/* Отрисовываем путь */}
      {routePoints && (
        <RouteLine
          points={routePoints}
          direction={direction}
          selectedSegmentId={selectedSegmentId}
          floorVisibility={floorVisibility}
        />
      )}
    </>
  );
});

const Scene = memo(function Scene() {
  const controlsRef = useRef<any>(null);
  const {
    currentFloor,
    setCurrentFloor,
    route,
    setDirection,
    direction,
    selectedSegmentId,
    setSelectedSegmentId,
    focusFullRoute,
    setFocusFullRoute,
    isCameraAnimating,
    setIsCameraAnimating,
    setIsRouteLoading,
    isFloorManuallySelected,
  } = useMapRouteStore(
    useShallow((s) => ({
      currentFloor: s.currentFloor,
      setCurrentFloor: s.setCurrentFloor,
      route: s.route,
      setDirection: s.setDirection,
      direction: s.direction,
      selectedSegmentId: s.selectedSegmentId,
      setSelectedSegmentId: s.setSelectedSegmentId,
      focusFullRoute: s.focusFullRoute,
      setFocusFullRoute: s.setFocusFullRoute,
      isCameraAnimating: s.isCameraAnimating,
      setIsCameraAnimating: s.setIsCameraAnimating,
      setIsRouteLoading: s.setIsRouteLoading,
      isFloorManuallySelected: s.isFloorManuallySelected,
    })),
  );
  const { buildingRef, bbox, cameraPosition, cameraTarget, onBuildingLoad } = useBuilding();

  // Скрываем Canvas пока камера не позиционирована (избегаем глитча при загрузке)
  const isReady = cameraPosition !== undefined;

  const { data, isFetching } = useQuery<Direction>({
    queryKey: ["get-direction", route.start, route.end],
    queryFn: async () =>
      api.get({
        url: `direction?from=${encodeURIComponent(route.start)}&to=${encodeURIComponent(route.end)}`,
        schema: directionSchema,
      }),
    enabled: !!route.start && !!route.end,
    retry: 0,
  });

  // Синхронизируем состояние загрузки с контекстом
  useEffect(() => {
    setIsRouteLoading(isFetching);
  }, [isFetching]);

  // React to query results (type-safe)
  useEffect(() => {
    if (!data) return;
    // Сбрасываем выбранный сегмент перед установкой нового маршрута
    setSelectedSegmentId(null);
    setDirection(data);

    console.log("Fetched direction data:", data);
  }, [data, setDirection, setSelectedSegmentId]);

  // Keep a small effect only to clear path when route is absent
  useEffect(() => {
    if (!route.start || !route.end) {
      setDirection(null);
    }
  }, [route, setDirection]);

  // Вычисляем видимость этажей для линии маршрута
  const floorVisibility = useMemo(() => {
    const highlightState = computeHighlightState(
      direction,
      selectedSegmentId,
      currentFloor,
      isFloorManuallySelected,
    );
    return computeFloorVisibility(highlightState);
  }, [direction, selectedSegmentId, currentFloor, isFloorManuallySelected]);

  // Мемоизируем points массив чтобы не создавать новый при каждом рендере
  const routePoints = useMemo(() => {
    if (!direction?.points?.length) return null;
    return direction.points.map(
      (p) => [p.x, p.z, -p.y] as [number, number, number],
    );
  }, [direction?.points]);

  return (
    <>
      <Canvas
        style={{
          width: "100vw",
          height: "100vh",
          position: "relative",
          zIndex: 0,
          opacity: isReady ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      >
        <SceneContent
          controlsRef={controlsRef}
          cameraPosition={cameraPosition}
          cameraTarget={cameraTarget}
          currentFloor={currentFloor}
          isCameraAnimating={isCameraAnimating}
          bbox={bbox}
          direction={direction}
          selectedSegmentId={selectedSegmentId}
          focusFullRoute={focusFullRoute}
          setFocusFullRoute={setFocusFullRoute}
          setCurrentFloor={setCurrentFloor}
          setIsCameraAnimating={setIsCameraAnimating}
          buildingRef={buildingRef}
          isFloorManuallySelected={isFloorManuallySelected}
          onBuildingLoad={onBuildingLoad}
          routePoints={routePoints}
          floorVisibility={floorVisibility}
        />
      </Canvas>
    </>
  );
});

export default Scene;
